/**
 * MyDayCard — Section 31 · Home timeline aggregating today's bookings.
 * Fetches from GET /api/my-day (backend joins existing collections).
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CalendarDays, ChevronRight, Sparkles } from "lucide-react";

export default function MyDayCard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api.get("/my-day")
      .then(r => { if (!cancelled) setData(r.data); })
      .catch(() => { if (!cancelled) setData({ items: [], empty_copy: "" }); });
    return () => { cancelled = true; };
  }, [user]);
  if (!user) return null;                        // Only for signed-in users
  if (!data) return null;                        // Loading — soft skip

  return (
    <section className="max-w-3xl mx-auto px-4 mt-4" data-testid="my-day-card">
      <div className="bg-white rounded-2xl shadow-sm border border-stone-200">
        <div className="flex items-center justify-between px-4 py-3 border-b border-stone-100">
          <div className="flex items-center gap-2">
            <CalendarDays size={18} className="text-emerald-700"/>
            <div className="font-black text-stone-900">My Day</div>
            <span className="text-[11px] text-stone-400">{data.date}</span>
          </div>
          <Link to="/dashboard" className="text-[11px] text-emerald-700 font-bold underline" data-testid="my-day-all">
            All →
          </Link>
        </div>
        {data.items.length === 0 ? (
          <div className="p-6 text-center text-sm text-stone-500" data-testid="my-day-empty">
            <Sparkles size={20} className="mx-auto mb-2 text-stone-300"/>
            {data.empty_copy}
          </div>
        ) : (
          <div className="divide-y divide-stone-100">
            {data.items.map(ev => (
              <Link key={ev.id} to={ev.action_url}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-stone-50 transition"
                    data-testid={`my-day-event-${ev.id}`}>
                <div className="text-xs font-mono text-stone-500 w-14 shrink-0">
                  {new Date(ev.at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-bold text-stone-800 truncate">{ev.title}</div>
                  <div className="text-[11px] text-stone-500 truncate">{ev.subtitle}</div>
                </div>
                <div className="text-[10px] uppercase tracking-widest font-black text-emerald-700 shrink-0">
                  {ev.status}
                </div>
                <ChevronRight size={14} className="text-stone-300 shrink-0"/>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
